import {
	useState, useEffect, useCallback, useMemo,
	useRef,
} from 'react';
import { debounce } from 'lodash';
import { Model } from 'Models/Model';

// @deprecated - use useEntityChangeTracker
export default function useHasChanged<T>(
	EntityClass: any,
	entity: T,
	fields: string[],
	comparator: (oldValue: any, newValue: any) => boolean,
	extraFieldDependencies: string[],
	extraEquivalenceCheck?: (copy: T, original: T) => boolean,
) {
	const copyEntity = useMemo(() => new EntityClass(entity), [entity, EntityClass]);
	const [equivalent, setEquivalent] = useState(true);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const checkEquality = useCallback(
		debounce(() => {
			const equivalentCheckResult = !!extraEquivalenceCheck
				? extraEquivalenceCheck(copyEntity, entity)
				: true;

			// Loop through fields, compare original to modified entity to see if there is any field that is not equal
			const comparatorCheckResult = fields.find(field => comparator(getFieldIfExists(field, copyEntity), getFieldIfExists(field, entity)));;
			setEquivalent(equivalentCheckResult || !comparatorCheckResult);
		}, 300),
		[copyEntity, entity],
	);

	function getFieldIfExists(field: string, ent: T) {
		if (ent && field in ent) return ent[field];
		return null;
	}

	// we enable save whenever something has changed, so we use a useEffect to do this watching on the specific fields
	useEffect(() => {
		checkEquality();
	}, [
		copyEntity, entity, checkEquality, extraEquivalenceCheck,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		...fields.map(f => getFieldIfExists(f, copyEntity)),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		...fields.map(f => getFieldIfExists(f, entity)),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		...extraFieldDependencies.map(f => getFieldIfExists(f, entity)),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		...extraFieldDependencies.map(f => getFieldIfExists(f, copyEntity)),
	]);

	return equivalent;
}

/**
 * Usage
 * - const PrimaryBusinessModifiableFields = ['name', 'abn', 'acn', 'trusteeACN', 'phone', 'dateOfBirth', 'trusteeName', 'email'] as (keyof BusinessEntity)[];
 * - { ... } = useEntityChangeTracker(BusinessEntity, businessEntity, PrimaryBusinessModifiableFields)
 */
type Constructor<T> = new (data: Partial<T>) => T;

type ChangedField<T> = {
	fieldName: keyof T;
	oldValue: T[keyof T];
	newValue: T[keyof T];
	// Incluude type for debugging as a date string and date object will look the same but wont be equal
	oldValueType: string;
	newValueType: string;
};

/**
 * 
 * 
 * Debugging change state
 * - console.log(JSON.stringify(getChangedFields()))
 * - This will show you the initial value and the changed value and meta data that will show you what may be different
 */
export function useEntityChangeTracker<T>(
	EntityClass: Constructor<T>,
	entity: T,
	fieldsToTrack: (keyof T)[]
) {
	// Track original value so we can do comparisons
	const originalRef = useRef<T>(new EntityClass(entity));

	const isChanged = useMemo(() => {
		return fieldsToTrack.some(field => !isEqual(entity[field], originalRef.current[field]));
	}, [
		entity, EntityClass,
		// If fields we are interested in changes of the value of that field changes, recalculate the state
		fieldsToTrack, ...fieldsToTrack.map(field => entity[field])
	]);

	function getValueType(value: any): string {
		if (value instanceof Date) return 'Date';
		return typeof value;
	}

	const changeSignature = useMemo(() => {
		const changed = fieldsToTrack
			.filter(field => !isEqual(entity[field], originalRef.current[field]))
			.map(field => ({
				field,
				original: originalRef.current[field],
				current: entity[field]
			}));
		return JSON.stringify(changed); // or use a proper hashing lib if it's big
	}, [
		entity, EntityClass,
		fieldsToTrack, ...fieldsToTrack.map(field => entity[field])
	]);

	/**
	 * Note comparison for objects may need to be done by intrinsic value as two objects may have all the same data but it will not equal as they are different referneces
	 */
	function isEqual(a: any, b: any): boolean {
		// Handle date typescurValue
		if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();

		// Handle our custom entities however, strip the created/modified field as we are interested in the actual fields we set
		if (a && b && a instanceof Model && b instanceof Model && a.constructor === b.constructor) {
			const replacer = (key: string, value: any) => {
				if (key === 'created' || key === 'modified') return undefined;
				return value;
			};

			try {
				return JSON.stringify(a, replacer) === JSON.stringify(b, replacer);
			} catch {
				return false;
			}
		}

		// Future: You will need to handle other object types as you use this functino more
		// If both are objects (not null), try to compare their JSON representations

		// Currently our inputs set things to undefined/null so when we do exact comparator, they are unequal
		if ((a == null || a == undefined) && (b == null || b == undefined)) return true; 
		
		// Handle other types.
		return a === b;
	}

	const getChangedFields = (): ChangedField<T>[] => {
		return fieldsToTrack
			.filter(field => !isEqual(entity[field], originalRef.current[field]))
			.map(field => ({
				fieldName: field,
				oldValue: originalRef.current[field],
				newValue: entity[field],
				oldValueType: getValueType(originalRef.current[field]),
				newValueType: getValueType(entity[field])
			}));
	};

	return {
		// Specifies whether the updated entity has different values to the original entity 
		isChanged,
		// State tracker so we know when values have changed.  
		// This is different to isChanged as isChanged remains on true when multiple value is changed.
		changeSignature,
		// Mostly for debugging
		getChangedFields,
	};
}